import { AnglesUpDown } from "@/icons/AnglesUpDown.tsx";
import classes from "@/ui/shared/StyleSelect.module.css";
import {
  CloseButton,
  Image,
  Popover,
  ScrollArea,
  TextInput,
} from "@mantine/core";
import { useDisclosure, useHotkeys } from "@mantine/hooks";
import { memo, ReactNode, useEffect, useRef } from "react";

export const OptionSelect = memo(function OptionSelect({
  label,
  value,
  setValue,
  map,
  photoMap,
  icon,
}: {
  label: string;
  value: string | "";
  setValue: (value: string | "") => void;
  map: Record<string, string>;
  photoMap: Record<string, string>;
  icon: ReactNode;
}) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [opened, { close, toggle }] = useDisclosure(false);

  function handleOnClick(event: MouseEvent) {
    if (
      dropdownRef.current &&
      !event.composedPath().includes(dropdownRef.current)
    ) {
      close();
    }
  }

  useEffect(() => {
    if (!opened) return;
    document.body.addEventListener("click", handleOnClick);
    return () => {
      document.body.removeEventListener("click", handleOnClick);
    };
  }, [opened]);

  useHotkeys([
    [
      "escape",
      () => {
        close();
      },
    ],
  ]);

  const rows = Math.round(Object.keys(map).length / 3);
  const height = Math.min(rows * 140, 420);

  const items = (
    <div className="flex flex-wrap gap-2 items-center justify-center">
      {Object.keys(map).map((key) => (
        <div
          key={key}
          onClick={() => {
            setValue(key);
            close();
          }}
          className="flex flex-col items-center p-1 relative cursor-pointer text-sm"
        >
          {key === value && <div className={classes.indicator} />}
          <Image
            className="z-[2]"
            src={photoMap[key]}
            radius="sm"
            w={94}
            h={94}
          />
          {key === value && (
            <span className={classes.selected}>{map[key]}</span>
          )}
          {key !== value && (
            <span className={classes.deselected}>{map[key]}</span>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <Popover opened={opened} onClose={close} width={360} shadow="md" withArrow>
      <Popover.Target>
        <TextInput
          size="md"
          styles={{
            input: { cursor: "pointer" },
          }}
          onClick={toggle}
          value={value ? map[value] : ""}
          leftSection={
            value ? (
              <Image src={photoMap[value]} radius="sm" w={30} h={30} />
            ) : (
              icon
            )
          }
          rightSection={
            value ? (
              <CloseButton onClick={() => setValue("")} />
            ) : (
              <AnglesUpDown />
            )
          }
          label={label}
          readOnly
        />
      </Popover.Target>
      <Popover.Dropdown ref={dropdownRef} onMouseLeave={close}>
        {rows > 2 ? (
          <ScrollArea
            h={height}
            type="hover"
            offsetScrollbars
            scrollbarSize={4}
          >
            {items}
          </ScrollArea>
        ) : (
          items
        )}
      </Popover.Dropdown>
    </Popover>
  );
});
